import { Injectable, OnDestroy } from '@angular/core'
import { BehaviorSubject, Observable, Subscription } from 'rxjs'
import { map } from 'rxjs/operators'
import { MapRealtimeGateway } from './map-realtime.gateway'
import { GoRealtimeClient } from './go-realtime.client'
import { RealtimeSubscriptionRegistry } from './realtime-subscription.registry'
import { TrackerHistoryPayload } from '@app/modules/client/map/shared/models/tracker-history-payload.model'
import { SocketState, SocketStatus } from '@app/modules/client/map/shared/abstractSocket.directive'

let seq = 0

@Injectable()
export class GoMapSocketAdapter extends MapRealtimeGateway implements OnDestroy {
  private readonly consumerId = `map-${++seq}`
  private readonly stateSubject: BehaviorSubject<SocketState>
  private readonly stateSub: Subscription
  private readonly coordinates$: Observable<TrackerHistoryPayload>
  socketState$: Observable<SocketState>

  constructor(
    private client: GoRealtimeClient,
    private registry: RealtimeSubscriptionRegistry,
  ) {
    super()
    this.client.connect()
    this.coordinates$ = this.client.position$.pipe(map((payload) => this.sanitize(payload)))
    this.stateSubject = new BehaviorSubject<SocketState>({ status: SocketStatus.Disconnect })
    this.stateSub = this.client.state$
      .pipe(map((s) => ({ status: s === 'connected' ? SocketStatus.Connect : SocketStatus.Disconnect })))
      .subscribe(this.stateSubject)
    this.socketState$ = this.stateSubject.asObservable()
  }

  isConnected(state: SocketState): boolean {
    return state.status === SocketStatus.Connect
  }

  subscribe(vehicleIds: number[]): void {
    this.registry.register(this.consumerId, vehicleIds ?? [])
  }

  getCoordinates(): Observable<TrackerHistoryPayload> {
    return this.coordinates$
  }

  close(): void {
    this.registry.release(this.consumerId)
  }

  ngOnDestroy(): void {
    this.stateSub.unsubscribe()
    this.stateSubject.complete()
    this.registry.release(this.consumerId)
  }

  private sanitize(payload: TrackerHistoryPayload): TrackerHistoryPayload {
    const coordinates = (payload?.coordinates ?? []).filter((c) => Number.isFinite(c?.lat) && Number.isFinite(c?.lng))
    return { ...payload, coordinates }
  }
}
